import React, { useMemo } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Box, Chip, Paper, Typography } from "@mui/material";

function money(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function formatMillions(value) {
  return `${(Number(value || 0) / 1_000_000).toFixed(2)}M`;
}

function formatThousands(value) {
  return `${(Number(value || 0) / 1_000).toFixed(2)}K`;
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(2)}%`;
}

function formatFullMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getLastTenMonthsIncludingCurrent() {
  const start = moment().startOf("month").subtract(9, "months");

  return Array.from({ length: 10 }, (_, index) => {
    const current = start.clone().add(index, "months");

    return {
      monthKey: current.format("YYYY-MM"),
      monthLabel: current.format("MMM YYYY"),
      start: current.clone().startOf("month").format("YYYY-MM-DD"),
      end: current.clone().endOf("month").format("YYYY-MM-DD"),
    };
  });
}

function calculateInvestmentRow(investment, interestStart, interestEnd) {
  let cost = money(investment.cost);
  const impairment = money(investment.impaired_amount);
  const total_interest = money(investment.total_interest);
  const daily_interest = money(investment.daily_interest);

  const purchase_date = moment(investment.purchase_date).format("YYYY-MM-DD");
  const maturity_date = moment(investment.maturity_date).format("YYYY-MM-DD");

  const tenure = Number(investment.tenure || 0);
  const rate_type = investment.rate_type;

  let status = moment(maturity_date).isAfter(moment()) ? "Active" : "Matured";

  let days_earned = moment().diff(moment(purchase_date), "days") + 1;
  let interest_charged = days_earned * daily_interest;
  let days_unearned = tenure - days_earned;
  let cumulative_interest = days_earned * daily_interest;
  let interest_unearned = days_unearned * daily_interest;

  let fair_value =
    rate_type === "interest rate"
      ? cost + cumulative_interest - impairment
      : cost - impairment;

  if (status === "Matured") {
    days_earned = moment(maturity_date).diff(moment(purchase_date), "days");
    days_unearned = 0;
    cumulative_interest = days_earned * daily_interest;
    interest_unearned = 0;
    interest_charged = 0;

    fair_value =
      rate_type === "interest rate"
        ? cost + cumulative_interest - impairment
        : cost - impairment;
  }

  if (interestStart && interestEnd) {
    const start = moment(interestStart);
    const end = moment(interestEnd);
    const purchase = moment(purchase_date);
    const maturity = moment(maturity_date);

    if (
      purchase.isBefore(start) &&
      maturity.isBefore(start) &&
      status === "Matured"
    ) {
      cumulative_interest = cost;
      interest_unearned = 0;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;
    }

    if (end.isBefore(purchase)) {
      cost = 0;
      fair_value = 0;
      interest_unearned = 0;
      cumulative_interest = 0;
      days_earned = 0;
      days_unearned = 0;
      interest_charged = 0;
      status = "investment was not available then";
    }

    if (purchase.isSameOrBefore(start) && maturity.isAfter(end)) {
      const diff = end.diff(start, "days");
      const cummDateDiff = end.diff(purchase, "days");

      days_earned = cummDateDiff + 1;
      days_unearned = tenure - (cummDateDiff + 1);
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = (diff + 1) * daily_interest;
    }

    if (
      purchase.isBefore(start) &&
      maturity.isSameOrAfter(start) &&
      maturity.isSameOrBefore(end)
    ) {
      const diff = maturity.diff(start, "days");
      const cummDateDiff = maturity.diff(purchase, "days");

      days_earned = cummDateDiff;
      days_unearned = tenure - cummDateDiff;
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = cummDateDiff * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = diff * daily_interest;
    }

    if (maturity.isBefore(start)) {
      interest_unearned = 0;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;
    }

    if (
      purchase.isAfter(start) &&
      purchase.isSameOrBefore(end) &&
      maturity.isAfter(end)
    ) {
      const diff = end.diff(purchase, "days");
      const cummDateDiff = end.diff(purchase, "days");

      days_earned = cummDateDiff + 1;
      days_unearned = tenure - (cummDateDiff + 1);
      interest_unearned = days_unearned * daily_interest;
      cumulative_interest = days_earned * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      interest_charged = (diff + 1) * daily_interest;
    }

    if (purchase.isSameOrAfter(start) && maturity.isSameOrBefore(end)) {
      const cummDateDiff = maturity.diff(purchase, "days");

      days_earned = cummDateDiff;
      days_unearned = 0;
      interest_unearned = 0;
      cumulative_interest = tenure * daily_interest;

      fair_value =
        rate_type === "interest rate"
          ? cost + cumulative_interest - impairment
          : cost - impairment;

      const diff = maturity.diff(purchase, "days");
      interest_charged = diff * daily_interest;
    }
  }

  return {
    cost,
    interest_charged,
    fair_value,
    total_interest,
    cumulative_interest,
    interest_unearned,
    daily_interest,
    status,
  };
}

export default function InvestmentInterestInsightTable() {
  const approvedInvestments = useSelector((state) => state.approvedInvestment);

  const months = useMemo(() => getLastTenMonthsIncludingCurrent(), []);

  const tableData = useMemo(() => {
    if (!Array.isArray(approvedInvestments)) return [];

    const baseData = months.map((month) => {
      const detailedRows = approvedInvestments
        .filter((investment) => {
          const purchaseDate = moment(investment.purchase_date);
          return purchaseDate.isSameOrBefore(moment(month.end));
        })
        .map((investment) =>
          calculateInvestmentRow(investment, month.start, month.end),
        )
        .filter((row) => Math.abs(Number(row.interest_charged || 0)) > 0.0001);

      const totalCost = detailedRows.reduce(
        (sum, row) => sum + money(row.cost),
        0,
      );

      const totalInterestCharged = detailedRows.reduce(
        (sum, row) => sum + money(row.interest_charged),
        0,
      );

      const interestYield =
        totalCost > 0 ? (totalInterestCharged / totalCost) * 100 : 0;

      const interestPerMillion =
        totalCost > 0 ? totalInterestCharged / (totalCost / 1_000_000) : 0;

      return {
        month: month.monthLabel,
        monthKey: month.monthKey,
        start: month.start,
        end: month.end,
        investmentCount: detailedRows.length,
        cost: totalCost,
        interestCharged: totalInterestCharged,
        interestYield,
        interestPerMillion,
      };
    });

    const averageInterest =
      baseData.length > 0
        ? baseData.reduce((sum, item) => sum + money(item.interestCharged), 0) /
          baseData.length
        : 0;

    return baseData.map((item, index) => {
      const previous = baseData[index - 1];

      const interestGrowth =
        previous && previous.interestCharged > 0
          ? ((item.interestCharged - previous.interestCharged) /
              previous.interestCharged) *
            100
          : 0;

      return {
        ...item,
        interestGrowth,
        performance:
          item.interestCharged >= averageInterest
            ? "Above Average"
            : "Below Average",
      };
    });
  }, [approvedInvestments, months]);

  const displayTableData = useMemo(() => {
    return [...tableData].reverse();
  }, [tableData]);

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        width: "50%",
        borderRadius: "18px",
        border: "1px solid #e2e8f0",
        overflow: "hidden",
        backgroundColor: "#fff",
        boxShadow: "0 7px 18px rgba(15,23,42,0.035)",
      }}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
          }}
        >
          <Box component="thead">
            <Box
              component="tr"
              sx={{
                backgroundColor: "#fff",
              }}
            >
              {[
                "Month",
                "Cost",
                "Interest Charged",
                "Yield",
                "MoM Growth",
                "Performance",
              ].map((header) => (
                <Box
                  key={header}
                  component="th"
                  sx={{
                    px: 1.5,
                    py: 1.2,
                    textAlign: "left",
                    fontSize: 10,
                    fontWeight: 950,
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    borderBottom: "1px solid #e2e8f0",
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </Box>
              ))}
            </Box>
          </Box>

          <Box component="tbody">
            {displayTableData.map((row) => {
              const isPositiveGrowth = row.interestGrowth >= 0;
              const isAboveAverage = row.performance === "Above Average";

              return (
                <Box
                  key={row.monthKey}
                  component="tr"
                  sx={{
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                    },
                  }}
                >
                  <Box
                    component="td"
                    sx={{
                      px: 1,
                      py: 1,
                      fontSize: 10,
                      fontWeight: 900,
                      color: "#1e293b",
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.month}
                  </Box>

                  <Box
                    component="td"
                    title={formatFullMoney(row.cost)}
                    sx={{
                      px: 1,
                      py: 1,
                      fontSize: 10,
                      fontWeight: 900,
                      color: "#355c80",
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatMillions(row.cost)}
                  </Box>

                  <Box
                    component="td"
                    title={formatFullMoney(row.interestCharged)}
                    sx={{
                      px: 1,
                      py: 1,
                      fontSize: 10,
                      fontWeight: 900,
                      color: "#17b859",
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatThousands(row.interestCharged)}
                  </Box>

                  <Box
                    component="td"
                    sx={{
                      px: 1,
                      py: 1,
                      fontSize: 10,
                      fontWeight: 800,
                      color: "#334155",
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatPercent(row.interestYield)}
                  </Box>

                  <Box
                    component="td"
                    sx={{
                      px: 1,
                      py: 1,
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Chip
                      size="small"
                      label={formatPercent(row.interestGrowth)}
                      sx={{
                        height: 20,
                        fontSize: 9,
                        fontWeight: 900,
                        borderRadius: "999px",
                        color: isPositiveGrowth ? "#047857" : "#b91c1c",
                        backgroundColor: isPositiveGrowth
                          ? "rgba(23,184,89,0.12)"
                          : "rgba(239,68,68,0.10)",
                      }}
                    />
                  </Box>

                  <Box
                    component="td"
                    sx={{
                      px: 1,
                      py: 1,
                      borderBottom: "1px solid #e2e8f0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Chip
                      size="small"
                      label={row.performance}
                      sx={{
                        height: 20,
                        fontSize: 9,
                        fontWeight: 900,
                        borderRadius: "999px",
                        color: isAboveAverage ? "#355c80" : "#92400e",
                        backgroundColor: isAboveAverage
                          ? "rgba(53,92,128,0.10)"
                          : "rgba(245,158,11,0.13)",
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {displayTableData.length === 0 && (
        <Box
          sx={{
            p: 2.5,
            textAlign: "center",
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 900,
              color: "#334155",
            }}
          >
            No monthly investment analysis found
          </Typography>

          <Typography sx={{ mt: 0.4, fontSize: 12, color: "#64748b" }}>
            Approved investments were not found for the past 10 months.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
